import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  Accordion,
  AccordionItem,
  AccordionItemBody,
  AccordionItemHead,
  Body2,
  Caption1,
  Header2,
  Header3,
  Paper,
  Link,
  PlayCircleOutlineIcon,
} from "@activecollab/components";
import ErrorBoundary from "~/components/ErrorBoundry";
import { Page } from "~/components/Page";
import GettingStartedSteps from "~/pages/GettingStarted/GettingStartedSteps";
import VimeoPlayer from "~/pages/GettingStarted/VimeoPlayer";
import { getLoggedUser } from "~/store/selectors/selector.users";
import Angie from "~/utils/angie";
import { assets_url } from "~/utils/urls";

const GettingStartedPage = () => {
  const { t } = useTranslation();
  const loggedUser = useSelector(getLoggedUser);
  const [videoId, setVideoId] = useState(null);
  const [videoTitle, setVideoTitle] = useState("");
  const [videoDesc, setVideoDesc] = useState("");
  const [open, setOpen] = useState(false);

  const closeVideo = useCallback(() => {
    setVideoId(null);
    setOpen(false);
  }, []);
  const accordionItems = useMemo(() => {
    return [
      {
        header: t("Projects and Profitability Overview"),
        body: t(
          "Get everything you need to organize tasks, and keep your team aligned. Monitor project progress and budgets to ensure profitability."
        ),
        videoBody: t(
          "In ActiveCollab, you can create an unlimited number of projects. Learn how to structure work, track time spent on tasks, manage files, keep an eye on key metrics, and encourage team collaboration."
        ),
        id: "projects",
        image: `${Angie.assets_url}/onboarding/images/ProjectsAndProfitability.jpg`,
        logDescription: "profitability",
        videoId: "1104280520",
      },
      {
        header: t("Time Tracking and Timesheets"),
        body: t(
          "Track billable and non-billable hours, and see how your team spends their time. Use insights for future adjustments."
        ),
        videoBody: t(
          "Time tracking helps you enhance accountability, adjust rates, and plan better. Check out how to track time across tasks and projects, compare time records against estimates, manage timesheets, and prioritize work."
        ),
        image: `${Angie.assets_url}/onboarding/images/TimeTrackingAndTimesheets.jpg`,
        id: "timetracking",
        logDescription: "timetracking",
        videoId: "1104280579",
      },
      {
        header: t("Workload and Capacity Planning"),
        body: t(
          "Manage team daily / weekly capacity and assignments to balance workload, prevent burnout, and plan ahead with confidence."
        ),
        videoBody: t(
          "Workload lets you see your team’s capacity and availability in real time. Discover how to navigate Workload, so you can ultimately optimize resource allocation and predict staffing needs."
        ),
        image: `${Angie.assets_url}/onboarding/images/WorkloadAndCapacityPlanning.jpg`,
        id: "workload",
        logDescription: "workload",
        videoId: "1104280635",
      },
      {
        header: t("People and Companies"),
        body: t(
          "Your team members, clients, and collaborators each have a dedicated space, where you’ll find all their relevant information."
        ),
        videoBody: t(
          "From setting roles and permissions to viewing team members’ availability and clients’ payment history: see how you can make the most of this section for people management!"
        ),
        image: `${Angie.assets_url}/onboarding/images/PeopleAndCompanies.jpg`,
        id: "people",
        logDescription: "people",
        videoId: "1104280689",
      },
      {
        header: t("Estimates"),
        body: t(
          "Create, send, and convert estimates into invoices with ActiveCollab: simplify quoting, win more work, and keep your cash flow steady."
        ),
        videoBody: t(
          "Estimates help you track sales opportunities, identify most demanded services, and refine pricing. Let us show you how you can create, send, and manage estimates in ActiveCollab! "
        ),
        image: `${Angie.assets_url}/onboarding/images/Estimates.jpg`,
        id: "estimates",
        logDescription: "estimates",
        videoId: "1104280742",
      },
      {
        header: t("Invoicing"),
        body: t(
          "Streamline invoicing, and keep an eye on your cash flow: create, customize, and send out invoices with a couple of clicks."
        ),
        videoBody: t(
          " Invoicing in ActiveCollab is straightforward and flexible. Find out how to generate personalized invoices, add payment links, and set up payment reminders to make sure you get paid."
        ),
        image: `${Angie.assets_url}/onboarding/images/Invoicing.jpg`,
        id: "invoicing",
        logDescription: "invoicing",
        videoId: "1104280783",
      },
      {
        header: t("Collaborative Calendar"),
        body: t(
          "Use the Calendar as your team’s hub for better time and workload management, team collaboration, and structure."
        ),
        videoBody: t(
          "Did you know that you could add calendar events to Workload? This gives you a better picture of your team’s actual availability. Watch the video for more things that make our Calendar stand out."
        ),
        image: `${Angie.assets_url}/onboarding/images/CollaborativeCalendar.jpg`,
        id: "calendar",
        logDescription: "calendar",
        videoId: "1104280829",
      },
      {
        header: t("Reports and Dashboards"),
        body: t(
          "Build custom reports and refer to dashboards to track project progress, analyze trends, and inform strategic decision-making."
        ),
        videoBody: t(
          "Reports give you insight into the most important aspects of your business. Here’s a brief overview of this powerful corner, where you can dissect and analyze every key aspect of your business."
        ),
        image: `${Angie.assets_url}/onboarding/images/ReportsAndDashboard.jpg`,
        id: "reports",
        logDescription: "reports",
        videoId: "1104280877",
      },
    ];
  }, [t]);

  const handleLogForAction = useCallback((resource) => {
    Angie.logger.info(
      "Getting started resource clicked.",
      {
        resource: resource,
      },
      true
    );
  }, []);

  const handleLogForVideoAction = useCallback((resource) => {
    Angie.logger.info(
      "Getting started video played.",
      {
        video: resource,
      },
      true
    );
  }, []);

  const playVideo = useCallback(
    (item) => {
      setVideoId(item?.videoId);
      setVideoTitle(item?.header);
      setVideoDesc(item?.videoBody);
      setOpen(true);
      handleLogForVideoAction(item?.logDescription);
    },
    [handleLogForVideoAction]
  );

  const resolveGreeting = useMemo(() => {
    const name = t("Hello");
    return `${name}, ${loggedUser?.first_name} 👋`;
  }, [loggedUser?.first_name, t]);

  return (
    <ErrorBoundary errorLocation="gettingStartedPage">
      <Page title="Getting Started">
        <Page.Header
          title={resolveGreeting}
          className="print:tw-hidden"
        />
        <Paper
          variant="paper-1"
          className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-[3fr_2fr] tw-grid-rows-[1fr_auto] tw-p-5 tw-mb-3 tw-gap-3"
        >
          <GettingStartedSteps />
          <div className="tw-bg-theme-200 tw-py-3 tw-px-6">
            <Header2 className="tw-border-solid tw-border-theme-400 tw-border-0 tw-border-b tw-pb-3">
              {t("Discover ActiveCollab")}
            </Header2>
            <Accordion
              className="tw-border-none"
              preExpanded={["projects"]}
            >
              {accordionItems?.map((item) => {
                return (
                  <AccordionItem
                    className="tw-bg-theme-200 tw-border-none tw-my-2"
                    key={item.header}
                    id={item?.id}
                  >
                    <AccordionItemHead className="tw-px-0 tw-mb-2">
                      <Header3>{item.header}</Header3>
                    </AccordionItemHead>
                    <AccordionItemBody className="tw-flex tw-flex-col sm:tw-flex-row tw-gap-6 tw-overflow-hidden tw-items-start">
                      <div
                        className="tw-relative tw-cursor-pointer"
                        onClick={() => {
                          playVideo(item);
                        }}
                      >
                        <img
                          src={item?.image}
                          alt={t("Main Navigation")}
                          className="tw-h-[85px] tw-rounded-lg tw-opacity-50 tw-relative"
                        />
                        <PlayCircleOutlineIcon
                          className="tw-shrink-0 tw-absolute tw-top-1/2 tw-left-1/2 -tw-translate-y-1/2 -tw-translate-x-1/2"
                          fill="var(--color-primary)"
                        />
                      </div>
                      <Caption1 className="">{item.body}</Caption1>
                    </AccordionItemBody>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </div>
          <div className="tw-bg-theme-200 tw-py-3 tw-px-6 tw-flex tw-flex-col tw-justify-between">
            <div>
              <Header3>{t("Download Our Mobile Apps")}</Header3>
              <Body2>{t("Access your workspace, wherever you are.")}</Body2>
            </div>
            <div className="tw-flex tw-flex-col tw-gap-2 tw-mt-5">
              <div className="tw-flex tw-gap-1 tw-items-center tw-h-11">
                <Link
                  href="https://apps.apple.com/us/app/activecollab-work-management/id1509421965"
                  target="_blank"
                  className="tw-flex tw-w-auto"
                >
                  <img
                    src={`${assets_url}/system/images/layout/download_app_from_app_store.png`}
                    alt={t("Download app from app store")}
                  />
                </Link>
                <Link
                  href="https://play.google.com/store/apps/details?id=com.activecollab.mobile"
                  target="_blank"
                  className="tw-flex tw-w-auto"
                >
                  <img
                    src={`${assets_url}/system/images/layout/download_app_from_google_play.png`}
                    alt={t("Download app from google play")}
                  />
                </Link>
              </div>
            </div>
          </div>
          <div className="tw-flex tw-flex-col tw-gap-4">
            <div className="tw-bg-theme-200 tw-px-6 tw-py-3 tw-flex tw-justify-between tw-items-center">
              <div>
                <Header3>{t("Subscribe to Our YouTube Channel")}</Header3>
                <Body2>{t("Get our latest product updates.")}</Body2>
              </div>
              <Link
                href="https://www.youtube.com/@activecollab"
                target="_blank"
                variant="option"
                className="tw-flex tw-w-[32px]"
                onClick={() => {
                  handleLogForAction("youtube");
                }}
              >
                <img
                  src={`${assets_url}/onboarding/images/YoutubeLogo.png`}
                  alt="YouTube Logo"
                  className="tw-w-[32px] tw-h-[32px]"
                />
              </Link>
            </div>
            <div className="tw-bg-theme-200 tw-px-6 tw-py-3 tw-flex tw-justify-between tw-items-center">
              <div>
                <Header3>{t("Let’s Connect")}</Header3>
                <Body2>{t("Follow us for great tips and insights!")}</Body2>
              </div>
              <div className="tw-flex tw-gap-3">
                <Link
                  href="https://www.linkedin.com/company/activecollab"
                  target="_blank"
                  variant="option"
                  className="tw-flex tw-w-[32px]"
                  onClick={() => {
                    handleLogForAction("linkedin");
                  }}
                >
                  <img
                    src={`${assets_url}/onboarding/images/LinkedinLogo.png`}
                    alt="Linkedin Logo"
                    className="tw-w-[32px] tw-h-[32px]"
                  />
                </Link>
                <Link
                  href="https://www.instagram.com/activecollab/"
                  target="_blank"
                  variant="option"
                  className="tw-flex tw-w-[32px]"
                  onClick={() => {
                    handleLogForAction("instagram");
                  }}
                >
                  <img
                    src={`${assets_url}/onboarding/images/InstagramLogo.png`}
                    alt="Instagram Logo"
                    className="tw-w-[32px] tw-h-[32px]"
                  />
                </Link>
                <Link
                  href="https://www.facebook.com/activecollab"
                  target="_blank"
                  variant="option"
                  className="tw-flex tw-w-[32px]"
                  onClick={() => {
                    handleLogForAction("facebook");
                  }}
                >
                  <img
                    src={`${assets_url}/onboarding/images/FacebookLogo.png`}
                    alt="Facebook Logo"
                    className="tw-w-[32px] tw-h-[32px]"
                  />
                </Link>
              </div>
            </div>
          </div>
        </Paper>
        {open ? (
          <VimeoPlayer
            videoId={videoId}
            title={videoTitle}
            description={videoDesc}
            open={open}
            onClose={closeVideo}
          />
        ) : null}
      </Page>
    </ErrorBoundary>
  );
};

export default GettingStartedPage;
